<template>
  <div class="shell-page today">
    <!-- 외부인(grade 1) 차단 -->
    <div v-if="isExternalUser" class="today__denied">
      <el-result
        icon="warning"
        title="접근 권한 없음"
        sub-title="시스템 접근 권한이 없습니다. 관리자에게 문의하여 권한을 요청하세요."
      >
        <template #extra>
          <el-button type="primary" @click="$router.push('/login')">로그인 페이지로</el-button>
        </template>
      </el-result>
    </div>

    <template v-else>
      <header class="today__hd">
        <p class="today__greeting">{{ greeting }}</p>
        <!-- 한 줄 요약. 둘 다 0 이면 "0건, 0건이 있습니다" 라는 이상한 문장이 되므로
             그때는 문장을 바꾼다 — 비어 있다는 사실 자체가 좋은 소식이다. -->
        <p class="today__line">
          <template v-if="remainingToday || pendingApprovals.length">
            오늘 남은 일 <b class="u-num">{{ remainingToday }}</b>건,
            결재를 기다리는 문서 <b class="u-num">{{ pendingApprovals.length }}</b>건이 있습니다
          </template>
          <template v-else>오늘 처리할 일도, 기다리는 결재도 없습니다</template>
        </p>
        <p class="today__date u-num">{{ todayLabel }}</p>
      </header>

      <!-- 요약 타일 — 탭하면 해당 목록으로.
           0건인 타일도 남긴다. 매일 보는 화면에서 타일이 사라졌다 나타나면
           위치가 흔들려 "왼쪽에서 두 번째" 라는 근육 기억이 통하지 않는다. -->
      <div class="tiles">
        <button
          type="button"
          class="tile tile--primary"
          :class="{ 'is-zero': !remainingToday }"
          @click="$router.push('/calendar?view=day')"
        >
          <span class="tile__label">오늘 남은 일</span>
          <span class="tile__value u-num">{{ remainingToday }}</span>
          <span class="tile__cap">전체 <span class="u-num">{{ todayItems.length }}</span>건 중</span>
        </button>

        <button
          type="button"
          class="tile"
          :class="{ 'is-zero': !summary.inProgress }"
          @click="goTasks({ scope: 'mine', status: 'in_progress' })"
        >
          <span class="tile__label">진행중</span>
          <span class="tile__value u-num">{{ summary.inProgress || 0 }}</span>
          <span class="tile__cap">내가 붙잡고 있는 업무</span>
        </button>

        <button
          type="button"
          class="tile tile--danger"
          :class="{ 'is-zero': !dueAlert }"
          @click="goTasks({ scope: 'mine' })"
        >
          <span class="tile__label">마감 임박</span>
          <span class="tile__value u-num">{{ dueAlert }}</span>
          <span class="tile__cap">
            지연 <span class="u-num">{{ summary.overdue || 0 }}</span>
            · 임박 <span class="u-num">{{ summary.dueSoon || 0 }}</span>
          </span>
        </button>

        <button
          type="button"
          class="tile tile--warn"
          :class="{ 'is-zero': !pendingApprovals.length }"
          @click="$router.push('/approvals')"
        >
          <span class="tile__label">결재 대기</span>
          <span class="tile__value u-num">{{ pendingApprovals.length }}</span>
          <span class="tile__cap">내 차례인 문서</span>
        </button>
      </div>

      <!-- 결재 대기: 0건이면 카드 자체를 렌더하지 않는다 -->
      <SectionCard
        v-if="canReport && pendingApprovals.length"
        title="내가 결재할 문서"
        :count="pendingApprovals.length"
        tone="warn"
        to="/approvals"
        action-label="결재함"
      >
        <div v-for="a in pendingApprovals" :key="a.stepId" class="apv">
          <button type="button" class="apv__main" @click="$router.push(`/reports/${a.report.id}`)">
            <el-avatar :size="30" :src="avatarOf(a.report.author)">{{ initialOf(a.report.author) }}</el-avatar>
            <span class="apv__body">
              <span class="apv__title u-trunc">{{ a.report.title }}</span>
              <span class="apv__sub u-trunc">{{ a.report.author?.nickname }} · {{ waitLabel(a.report.submittedAt) }}</span>
            </span>
            <span class="apv__wait dday only-mobile" :class="waitTone(a.report.submittedAt)">
              {{ waitLabel(a.report.submittedAt) }}
            </span>
          </button>

          <!-- 폰에서도 '승인' 은 낸다. 결재는 이 앱에만 있는 가치인데 여태
               도장이 데스크톱에만 있어서, 폰만 쓰는 결재자는 승인을 할 수 없었다.
               '반려' 는 폰에서 감춘다 — 사유가 필수라(approvalService.rejectStep 이
               사유 없으면 400) 좁은 화면에서 프롬프트를 띄우는 비용이 크고,
               오탭 비용도 승인보다 높다. 반려는 행을 눌러 상세에서 한다.
               승인 확인 다이얼로그(decide 의 ElMessageBox)는 그대로 유지한다. -->
          <div class="apv__acts">
            <button
              type="button"
              class="apvbtn apvbtn--reject only-tablet-up"
              :disabled="acting != null"
              @click="decide(a, 'reject')"
            >반려</button>
            <button
              type="button"
              class="apvbtn apvbtn--approve"
              :disabled="acting != null"
              @click="decide(a, 'approve')"
            >승인</button>
          </div>
        </div>
      </SectionCard>

      <!-- 날짜 없이 적어둔 업무.
           서버는 이걸 agenda 응답의 unscheduled 로 이미 따로 주고 있었는데
           홈이 includeUnscheduled 를 안 넘겨서 화면 어디에도 안 나왔다.
           마감을 임의로 붙여 '다가오는 마감' 에 끼워넣지 않는다 — 그러면
           그 카드가 "진짜 마감" 이라는 의미를 잃는다. 별도 목록으로 둔다.
           전체 목록은 /tasks 에 이미 나온다(기간 필터가 미완료를 날짜와
           무관하게 통과시킨다 — server/routes/tasks.js:135). -->
      <SectionCard
        v-if="unscheduled.length"
        title="적어둠"
        :count="unscheduled.length"
        to="/tasks?scope=mine"
        action-label="업무"
      >
        <button
          v-for="it in unscheduled.slice(0, 5)"
          :key="it.uid"
          type="button"
          class="due__row"
          @click="openItem(it)"
        >
          <span class="due__bar" :style="{ background: it.color }" />
          <span class="due__body">
            <span class="due__title u-trunc">{{ it.title }}</span>
            <span class="due__sub">
              <StatusPill :status="it.status" />
              <span v-if="it.department" class="u-trunc">{{ it.department.name }}</span>
            </span>
          </span>
        </button>
      </SectionCard>

      <!-- 오늘 일정 + 업무 (통합 아젠다) -->
      <SectionCard title="오늘" to="/calendar?view=day" action-label="캘린더">
        <div v-if="loading" class="skel">
          <div v-for="i in 3" :key="i" class="skel-row" />
        </div>

        <div v-else-if="todayItems.length" class="agenda">
          <template v-for="(it, i) in todayItems" :key="it.uid">
            <button
              type="button"
              class="agenda__row"
              :class="{ 'is-past': isPastItem(it) || isDoneItem(it) }"
              @click="openItem(it)"
            >
              <!-- 시각 칸. 업무는 시각 대신 체크박스 모양이 선다 —
                   같은 목록에 섞인 '약속' 과 '해야 할 일' 이 왼쪽 끝에서 갈린다.
                   (표시 전용이다. 완료 처리는 상세/캘린더 시트에서 한다) -->
              <span class="agenda__gutter">
                <span v-if="it.kind === 'task'" class="agenda__check" :class="{ 'is-done': it.status === 'done' }">
                  <el-icon v-if="it.status === 'done'"><Select /></el-icon>
                </span>
                <span v-else class="u-num">{{ it.allDay ? '종일' : timeLabel(it) }}</span>
              </span>

              <span class="agenda__bar" :style="{ background: it.color }" />

              <span class="agenda__body">
                <span class="agenda__title u-trunc">{{ it.title }}</span>
                <span class="agenda__sub">
                  <span v-if="it.kind === 'schedule' && it.typeLabel" :style="{ color: it.color }">{{ it.typeLabel }}</span>
                  <span v-if="it.kind === 'task' && !it.allDay && timeLabel(it)" class="u-num">{{ timeLabel(it) }}</span>
                  <span v-if="it.location" class="u-trunc">{{ it.location.name }}</span>
                  <span v-if="it.kind === 'task' && it.isOverdue" class="agenda__overdue">기한 지남</span>
                </span>
              </span>

              <span class="agenda__right">
                <StatusPill v-if="it.kind === 'task'" :status="it.status" />
              </span>
            </button>

            <!-- 현재 시각 라인. 이 카드의 핵심이다 — 위는 이미 지나간 것,
                 아래는 아직 남은 것. 종일 항목은 하루 내내 유효하므로
                 지난 것으로 치지 않는다(맨 위에 종일 하나만 있으면 선이 사라지던 문제). -->
            <div v-if="i === nowLineAfter" class="nowline" aria-hidden="true">
              <span class="nowline__time u-num">{{ nowLabel }}</span>
              <span class="nowline__rule" />
            </div>
          </template>
        </div>

        <!-- 빈 상태. emptyText 슬롯 폴백은 v-if 주석 노드 때문에 한 번도 뜬 적이 없다. -->
        <div v-else class="empty-state">
          <div class="empty-state__icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
              <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" /><path d="M3.5 10h17M8 3v4M16 3v4" />
            </svg>
          </div>
          <div class="empty-state__title">오늘 잡힌 일이 없습니다</div>
          <div class="empty-state__hint">캘린더에서 일정을, 업무에서 할 일을 추가할 수 있습니다</div>
        </div>
      </SectionCard>

      <!-- 마감과 미배정은 성격이 같은 '들여다볼 목록' 이라 데스크톱에서 나란히 둔다.
           auto-fit 이라 한쪽이 없으면(권한·0건) 남은 카드가 폭을 다 쓴다. -->
      <div class="grid2">
        <SectionCard
          v-if="upcoming.length"
          title="다가오는 마감"
          to="/tasks?scope=mine"
          action-label="업무"
        >
          <button
            v-for="it in upcoming"
            :key="it.uid"
            type="button"
            class="due__row"
            @click="openItem(it)"
          >
            <span class="due__bar" :style="{ background: it.color }" />
            <span class="due__body">
              <span class="due__title u-trunc">{{ it.title }}</span>
              <span class="due__sub">
                <StatusPill :status="it.status" />
                <span v-if="it.department" class="u-trunc">{{ it.department.name }}</span>
              </span>
            </span>
            <span class="dday" :class="ddayTone(it)">{{ dueLabel(it) }}</span>
          </button>
        </SectionCard>

        <!-- 미배정 업무 -->
        <UnassignedTasksCard v-if="canViewTeamData" />
      </div>

      <!-- 일정은 전용 상세 화면이 없다. 캘린더와 같은 정보 시트를 여기서 바로 연다. -->
      <EventInfoDialog v-model="eventInfoOpen" :event="viewingEvent" @refresh="refreshAfterEdit" />

      <!-- 보고 -->
      <SectionCard v-if="canReport" title="내 업무보고" to="/reports" action-label="전체">
        <div class="rep">
          <div class="rep__stats">
            <span v-for="s in reportStatuses" :key="s.key" class="rep__chip">
              <span class="rep__dot" :style="{ background: s.color }" />
              {{ s.label }} <b class="u-num">{{ s.count }}</b>
            </span>
          </div>
          <el-button class="rep__cta" type="primary" :plain="todayReportExists" @click="composeDaily">
            <el-icon v-if="todayReportExists"><Select /></el-icon>{{ todayReportExists ? '오늘 보고 완료' : '오늘 일일보고 작성' }}
          </el-button>
        </div>
      </SectionCard>
    </template>
  </div>
</template>

<script>
import { ref, computed, onMounted, onActivated, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { Select } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import SectionCard from '@/components/common/SectionCard.vue'
import StatusPill from '@/components/tasks/StatusPill.vue'
import UnassignedTasksCard from '@/components/dashboard/UnassignedTasksCard.vue'
import EventInfoDialog from '@/components/EventInfoDialog.vue'
import { getProfileImageUrl, getProfileInitial } from '@/utils/profileUtils'
import { ymd, addDaysYmd, timeLabel, itemsOnDate, parseServerDateTime } from '@/utils/agendaItem'
import { formatDueDate } from '@/utils/taskUtils'

/**
 * 홈 — "지금 내가 처리해야 할 것" 을 보여주는 인박스.
 *
 * 캘린더 탭과 역할이 겹치지 않게 유지하는 것이 중요하다.
 *   홈     = 나에게 필요한 액션 목록 (인박스)
 *   캘린더 = 누가 언제 무엇을 하나 (지도)
 * 이게 흐려지면 홈은 다시 통계 벽이 되고 아무도 안 본다.
 *
 * 이전 대시보드 대비:
 *  · 하루를 그리려고 ±30일(61일치) 이벤트를 받아오던 호출 제거
 *  · 요청 6개(fetchEvents ×2, taskSummary, myReports, pendingApprovals,
 *    taskBadges) → 아젠다 1 + 요약 3 으로 정리, 아젠다는 캐시를 탄다
 *  · 결재함 카드는 0건이면 아예 렌더하지 않는다. 12명 중 9명은 결재할 게 없고
 *    빈 카드는 폴드 위의 죽은 픽셀이다.
 *  · '오늘' 카드에 현재 시각 라인이 들어간다. 목록만 있을 때는 지난 일과 남은 일이
 *    같은 무게로 쌓여서, 지금이 몇 시인지 스스로 세어 가며 읽어야 했다.
 *  · 결재는 홈에서 바로 승인/반려한다(데스크톱). 예전에는 홈 → 결재함 → 상세로
 *    세 번 이동해야 도장 한 번을 찍을 수 있었다.
 */
export default {
  name: 'TodayPage',
  components: { SectionCard, StatusPill, UnassignedTasksCard, EventInfoDialog, Select },
  setup() {
    const store = useStore()
    const router = useRouter()

    const items = ref([])
    // 시작일·마감일이 둘 다 없는 내 업무. 서버가 agenda 응답의 unscheduled 로
    // 따로 주는데(server/routes/agenda.js:166) 홈이 여태 요청하지 않았다.
    const unscheduled = ref([])
    const loading = ref(false)

    /* 현재 시각. 라인 위치와 '남은 일' 개수가 여기서 파생되므로
       1분마다 갱신한다(초 단위로 돌릴 이유는 없다 — 표시는 HH:mm 이다). */
    const now = ref(new Date())
    let ticker = null

    /* '오늘' 도 now 에서 파생한다. 예전에는 setup 시점에 한 번 굳힌 상수였는데,
       홈은 keep-alive 라 탭을 켜 둔 채 자정을 넘기면 다음 날에도 어제 목록·어제 날짜·
       '좋은 저녁입니다' 가 그대로 남았다(돌아와 다시 불러와도 어제 날짜로 조회했다). */
    const todayYmd = computed(() => ymd(now.value))
    const isExternalUser = computed(() => store.state.user?.grade < 2)
    const canViewTeamData = computed(() => store.state.user?.isAdmin || store.state.user?.grade >= 2)
    const canReport = computed(() => store.getters.canReport)

    const greeting = computed(() => {
      const h = now.value.getHours()
      const g = h < 12 ? '좋은 아침입니다' : h < 18 ? '좋은 오후입니다' : '좋은 저녁입니다'
      return `${g}, ${store.state.user?.nickname || ''}님`
    })

    const todayLabel = computed(() => {
      const d = now.value
      const w = ['일', '월', '화', '수', '목', '금', '토'][d.getDay()]
      return `${d.getMonth() + 1}월 ${d.getDate()}일 (${w})`
    })

    const summary = computed(
      () => store.state.taskSummary || { pending: 0, inProgress: 0, done: 0, overdue: 0, dueSoon: 0 }
    )
    const dueAlert = computed(() => (summary.value.overdue || 0) + (summary.value.dueSoon || 0))
    const pendingApprovals = computed(() => store.state.pendingApprovals || [])

    /** 오늘 날짜를 덮는 항목 — 일정과 업무가 섞여 시간순으로 나온다 */
    const todayItems = computed(() => itemsOnDate(items.value, todayYmd.value))

    /** 오늘 이후 7일 안에 마감인 내 업무 (오늘 것은 위 카드에 이미 있다) */
    const upcoming = computed(() =>
      items.value
        .filter((it) => it.kind === 'task' && it.status !== 'done' && it.dueDate && it.dueDate > todayYmd.value)
        .sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''))
        .slice(0, 5)
    )

    /* 끝난 항목 판정을 둘로 나눈다.
       시각이 지난 것(isPastItem)과 완료된 업무(isDoneItem)는 둘 다 흐리게 그리지만,
       현재 시각 라인의 위치는 **시각만** 본다. 하나로 합쳐 두면 목록 맨 아래의
       완료 업무가 '마지막 지난 항목' 이 되어 라인이 통째로 사라졌다. */
    function isPastItem(it) {
      if (it.allDay) return false
      const at = it.end || it.start
      return !!at && at.getTime() < now.value.getTime()
    }
    function isDoneItem(it) {
      return it.kind === 'task' && it.status === 'done'
    }

    /** 지난 항목 중 마지막 인덱스. 그 뒤에 남은 게 없으면(-1) 라인을 그리지 않는다 */
    const nowLineAfter = computed(() => {
      const list = todayItems.value
      let last = -1
      list.forEach((it, i) => { if (isPastItem(it)) last = i })
      return last >= 0 && last < list.length - 1 ? last : -1
    })

    const nowLabel = computed(() => {
      const p = (n) => String(n).padStart(2, '0')
      return `${p(now.value.getHours())}:${p(now.value.getMinutes())}`
    })

    /** 헤더 한 줄과 첫 타일이 같이 쓰는 값 — 지나지도, 끝나지도 않은 오늘 항목 */
    const remainingToday = computed(
      () => todayItems.value.filter((it) => !isPastItem(it) && !isDoneItem(it)).length
    )

    // 서버는 { draft, in_progress, approved, rejected } 를 플랫하게 준다
    const myReportSummary = computed(() => store.state.myReportSummary || {})
    const reportStatuses = computed(() => {
      const c = myReportSummary.value
      return [
        { key: 'draft', label: '임시', count: c.draft || 0, color: 'var(--rpt-draft)' },
        { key: 'in_progress', label: '결재중', count: c.in_progress || 0, color: 'var(--rpt-in-progress)' },
        { key: 'approved', label: '승인', count: c.approved || 0, color: 'var(--rpt-approved)' },
        { key: 'rejected', label: '반려', count: c.rejected || 0, color: 'var(--rpt-rejected)' }
      ]
    })

    // 내 보고서 목록은 스토어에 커밋되지 않고 반환만 되므로 로컬에 담는다
    const myReports = ref([])
    const todayReportExists = computed(() =>
      myReports.value.some((r) => r.type === 'daily' && String(r.reportDate).slice(0, 10) === todayYmd.value)
    )

    function avatarOf(u) { return getProfileImageUrl(u) }
    function initialOf(u) { return getProfileInitial(u) }
    function goTasks(query) { router.push({ name: 'Tasks', query }) }
    function dueLabel(it) { return formatDueDate(it.dueDate) }

    /** D-day 배지 톤. 지남=빨강, 오늘·내일=앰버. 규칙은 primitives.css 의 .dday 와 같다 */
    function ddayTone(it) {
      if (it.isOverdue) return 'dday--over'
      const l = formatDueDate(it.dueDate)
      return l === '오늘' || l === '내일' || l === 'D-1' ? 'dday--soon' : ''
    }

    /** 상신 후 지난 날수. null 이면 상신 시각이 없다 */
    function waitDays(submittedAt) {
      const at = parseServerDateTime(submittedAt)
      return at ? Math.floor((Date.now() - at) / 86400000) : null
    }

    function waitLabel(submittedAt) {
      const days = waitDays(submittedAt)
      if (days == null) return ''
      if (days <= 0) return '오늘 요청'
      return `${days}일 대기`
    }

    /* 대기 배지 톤. 결재 SLA 가 따로 정의돼 있지 않아 눈으로 고른 경계다:
       하루 넘게 묵으면 앰버, 사흘 넘으면 빨강. */
    function waitTone(submittedAt) {
      const days = waitDays(submittedAt)
      if (days == null) return ''
      if (days >= 3) return 'dday--over'
      if (days >= 1) return 'dday--soon'
      return ''
    }

    /*
     * 일정은 그 자리에서 정보 시트를 연다. 예전에는 캘린더 일 뷰로 보냈는데
     * (a) 화면이 바뀌고도 그 일정은 열리지 않아 한 번 더 찾아 눌러야 했고
     * (b) 날짜를 시작일(it.date)로 넘겨서, 어제 시작한 출장을 누르면 어제로 갔다.
     */
    const eventInfoOpen = ref(false)
    const viewingEvent = ref(null)
    function openItem(it) {
      if (it.kind === 'task') {
        router.push(`/tasks/${it.id}`)
        return
      }
      viewingEvent.value = it
      eventInfoOpen.value = true
    }

    /* 정보 시트에서 수정/삭제했다. 아젠다 캐시(60초)를 비워야 바뀐 내용이 보인다. */
    async function refreshAfterEdit() {
      await store.dispatch('invalidateAgenda')
      await load()
    }

    function composeDaily() {
      router.push({ name: 'ReportCompose', query: { type: 'daily', date: todayYmd.value } })
    }

    /* 홈에서 끝내는 결재(데스크톱 전용 버튼).
       반려 사유는 서버(approvalService.rejectStep)가 400 으로 막고 있어 필수로 받는다.
       스와이프가 아니라 명시적 버튼 + 확인 단계인 이유: 승인에는 undo 경로가 없다. */
    const acting = ref(null)
    async function decide(a, mode) {
      const id = a.report?.id
      if (!id || acting.value != null) return
      let comment = ''
      try {
        if (mode === 'reject') {
          const r = await ElMessageBox.prompt('반려 사유를 적어주세요. 작성자에게 그대로 전달됩니다.', '반려', {
            confirmButtonText: '반려',
            cancelButtonText: '취소',
            inputType: 'textarea',
            inputPlaceholder: '예: 이번 주 실적 수치가 빠졌습니다',
            inputValidator: (v) => (v && String(v).trim() ? true : '반려 사유는 필수입니다'),
            confirmButtonClass: 'el-button--danger'
          })
          comment = r.value
        } else {
          await ElMessageBox.confirm(`'${a.report.title}' 을(를) 승인합니다. 승인은 되돌릴 수 없습니다.`, '승인', {
            confirmButtonText: '승인',
            cancelButtonText: '취소'
          })
        }
      } catch {
        return // 취소/닫기도 reject 로 오므로 조용히 빠진다
      }

      acting.value = a.stepId
      try {
        // 두 액션 모두 성공 후 fetchPendingApprovals 를 스스로 다시 부른다(스토어 계약)
        await store.dispatch(mode === 'approve' ? 'approveReport' : 'rejectReport', { id, comment })
        // 배지 갱신은 approveReport/rejectReport 안에서 이미 한다(store/modules/reports.js).
        // 여기서 또 부르면 결재 1건마다 /api/approvals/count 가 두 번 나간다.
        ElMessage.success(mode === 'approve' ? '승인했습니다.' : '반려했습니다.')
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '처리에 실패했습니다.')
      } finally {
        acting.value = null
      }
    }

    async function load() {
      if (!store.getters.isAuthenticated) {
        router.push('/login')
        return
      }
      loading.value = true
      now.value = new Date()
      try {
        // 오늘 ~ 7일 뒤면 "오늘"과 "다가오는 마감"을 한 번의 조회로 덮는다
        const from = todayYmd.value
        const to = addDaysYmd(from, 7)

        const [agenda] = await Promise.all([
          store.dispatch('fetchAgenda', { from, to, scope: 'mine', includeUnscheduled: true }),
          store.dispatch('fetchTaskSummary').catch(() => {}),
          canReport.value ? store.dispatch('fetchPendingApprovals').catch(() => {}) : null,
          canReport.value
            ? store.dispatch('fetchMyReports', { limit: 10 })
                .then((r) => { myReports.value = r?.reports || [] })
                .catch(() => {})
            : null
        ])
        items.value = agenda.items
        unscheduled.value = agenda.unscheduled || []
      } catch (e) {
        items.value = []
        unscheduled.value = []
        if (e.response?.status === 401) {
          store.commit('SET_USER', null)
          router.push('/login')
        }
      } finally {
        loading.value = false
      }
    }

    onMounted(async () => {
      // 카카오 로그인 직후(?login=success)의 checkAuth 는 라우터 가드가 화면에 들어오기 전에 끝내고 쿼리도 뗀다
      ticker = setInterval(() => { now.value = new Date() }, 60000)
      await load()
    })

    /* keep-alive 로 살아 있는 동안 다른 화면에서 돌아왔을 때 갱신.
       예전에는 `items.value.length` 로 첫 활성화(마운트 직후)를 거르려 했는데,
       그러면 오늘 일정이 0건인 사람은 돌아와도 영영 다시 불러오지 않았다 —
       다른 화면에서 일정을 만들고 와도 '오늘 잡힌 일이 없습니다' 가 남았다.
       캐시도 비운다. 업무 화면의 저장은 아젠다 캐시(60초)를 무효화하지 않아서,
       업무를 끝내고 돌아와도 1분 동안 '진행중' 으로 남았다. */
    let activatedOnce = false
    onActivated(() => {
      if (!activatedOnce) { activatedOnce = true; return }
      store.dispatch('invalidateAgenda')
      load()
    })

    // keep-alive 캐시에 있는 동안에는 호출되지 않는다. 1분 타이머라 그동안 돌아도
    // 비용이 없고, 화면을 다시 열었을 때 시각이 이미 맞아 있는 편이 낫다.
    onUnmounted(() => { if (ticker) clearInterval(ticker) })

    return {
      isExternalUser, canViewTeamData, canReport,
      greeting, todayLabel, loading, items, todayItems, upcoming, unscheduled,
      summary, dueAlert, pendingApprovals, reportStatuses, todayReportExists,
      nowLabel, nowLineAfter, remainingToday, isPastItem, isDoneItem, acting, decide,
      timeLabel, dueLabel, ddayTone, waitLabel, waitTone,
      avatarOf, initialOf, goTasks, openItem, composeDaily,
      eventInfoOpen, viewingEvent, refreshAfterEdit
    }
  }
}
</script>

<style scoped>
.today__hd { margin-bottom: var(--sp-4); }

.today__greeting {
  margin: 0;
  font-size: 22px;
  line-height: 1.3;
  letter-spacing: -0.02em;
  font-weight: var(--fw-semibold);
  color: var(--color-text);
}

/* 한 줄 요약. 숫자만 굵게 — 문장을 통째로 굵게 하면 헤더가 두 덩어리가 된다. */
.today__line {
  margin: var(--sp-2) 0 0;
  font-size: var(--fs-md);
  color: var(--color-text-muted);
}
.today__line b { color: var(--color-text); font-weight: var(--fw-semibold); }

.today__date {
  margin: 2px 0 0;
  font-size: var(--fs-sm);
  color: var(--color-text-subtle);
}

.today__denied { padding: var(--sp-12) var(--sp-4); text-align: center; }

/* ---- 요약 타일 ----
   좌측 3px 액센트 바가 색을 맡고 배경은 흰색으로 둔다.
   타일 네 개를 각자 옅은 색으로 칠했더니 화면 위쪽이 파스텔 줄무늬가 됐고,
   정작 중요한 '마감 임박' 이 나머지 셋과 같은 채도로 묻혔다. */
.tiles {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: var(--sp-3);
}

.tile {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-height: 84px;
  padding: var(--sp-3) var(--sp-3) var(--sp-3) calc(var(--sp-3) + 3px);
  border: 1px solid var(--color-border);
  border-radius: var(--r-lg);
  background: var(--color-surface);
  box-shadow: var(--sh-1);
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}
.tile:active { background: var(--color-surface-2); }

.tile::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--color-border-strong);
}
.tile--primary::before { background: var(--color-primary); }
.tile--danger::before { background: var(--color-danger); }
.tile--warn::before { background: var(--color-warning); }

.tile__label {
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--color-text-muted);
  white-space: nowrap;
}

.tile__value {
  font-size: var(--fs-stat);
  line-height: 1.1;
  font-weight: var(--fw-semibold);
  color: var(--color-text);
}

.tile__cap {
  font-size: var(--fs-xs);
  color: var(--color-text-subtle);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* 0건 타일은 자리는 지키되 조용해진다 — 없애 버리면 나머지 타일 위치가 흔들린다 */
.tile.is-zero .tile__value { color: var(--color-text-subtle); }
.tile.is-zero::before { background: var(--color-border); }

/* ---- 결재 행 ---- */
.apv {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  border-bottom: 1px solid var(--color-border);
}
.apv:last-child { border-bottom: 0; }

.apv__main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-2) 0;
  min-height: var(--tap-comfortable);
  border: 0;
  background: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}
.apv__main:active { background: var(--color-surface-2); }

.apv__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.apv__title { font-size: var(--fs-md); color: var(--color-text); }
.apv__sub { font-size: var(--fs-xs); color: var(--color-text-muted); }

.apv__acts { display: flex; align-items: center; gap: var(--sp-2); flex-shrink: 0; }

/* 승인/반려 버튼. 데스크톱에서는 34px — 카드 안에서 한 행에 둘이 들어가야 해서
   밀도를 따른다. 폰에서는 '승인' 하나만 뜨고 그건 터치 대상이므로
   아래 미디어쿼리에서 44px 규칙으로 올린다. */
.apvbtn {
  height: 34px;
  padding: 0 var(--sp-4);
  border-radius: var(--r-sm);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface);
  font-family: inherit;
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-muted);
  cursor: pointer;
  white-space: nowrap;
}
.apvbtn:hover { background: var(--color-surface-2); }
.apvbtn:disabled { opacity: 0.5; cursor: default; }

.apvbtn--reject { border-color: var(--color-danger-soft); color: var(--color-danger); }
.apvbtn--reject:hover { background: var(--color-danger-soft); }

.apvbtn--approve {
  border-color: var(--color-primary);
  background: var(--color-primary);
  color: var(--color-surface);
}
.apvbtn--approve:hover { background: var(--color-primary-hover); border-color: var(--color-primary-hover); }

/* 폰: 승인 버튼이 유일한 액션이고 손가락으로 눌린다. --tap-min 규칙을 따른다. */
@media (max-width: 767px) { /* --bp-mobile-max */
  .apvbtn { height: var(--tap-min); padding: 0 var(--sp-3); }
}

/* ---- 오늘 아젠다 ---- */
.agenda { display: flex; flex-direction: column; }

.agenda__row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-2) 0;
  min-height: var(--tap-comfortable);
  border: 0;
  border-bottom: 1px solid var(--color-border);
  background: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}
.agenda__row:last-child { border-bottom: 0; }
.agenda__row:active { background: var(--color-surface-2); }

/* 지나간 항목 — 지워지진 않되 남은 일과 무게가 같아서는 안 된다 */
.agenda__row.is-past { opacity: 0.55; }
.agenda__row.is-past .agenda__title { text-decoration: line-through; }

.agenda__gutter {
  width: 82px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  font-size: var(--fs-sm);
  color: var(--color-text-muted);
}

.agenda__check {
  width: 17px;
  height: 17px;
  border: 1.5px solid var(--color-border-strong);
  border-radius: var(--r-sm);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-xs);
  color: var(--color-surface);
}
.agenda__check.is-done { background: var(--color-success); border-color: var(--color-success); }

.agenda__bar { width: 3px; align-self: stretch; border-radius: var(--r-full); flex-shrink: 0; }

.agenda__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }

.agenda__title { font-size: var(--fs-md); color: var(--color-text); }

.agenda__sub {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: var(--fs-xs);
  color: var(--color-text-muted);
}
.agenda__overdue { color: var(--color-danger); font-weight: var(--fw-medium); }

.agenda__right { flex-shrink: 0; display: flex; align-items: center; }

/* 현재 시각 라인 */
.nowline {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-1) 0;
}
.nowline__time {
  width: 82px;
  flex-shrink: 0;
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-danger);
}
.nowline__rule {
  position: relative;
  flex: 1;
  height: 2px;
  border-radius: var(--r-full);
  background: var(--color-danger);
}
.nowline__rule::before {
  content: '';
  position: absolute;
  left: -3px;
  top: 50%;
  transform: translateY(-50%);
  width: 7px;
  height: 7px;
  border-radius: var(--r-full);
  background: var(--color-danger);
}

/* ---- 다가오는 마감 / 미배정 2열 ---- */
/* row-gap 은 0 이다 — SectionCard 가 이미 margin-bottom 을 갖고 있어서
   둘을 더하면 세로로 쌓이는 모바일에서만 카드 간격이 24px 로 벌어졌다.
   자식의 margin 을 :deep 으로 지우는 대신 여기서 안 더하는 쪽을 택한다. */
.grid2 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(320px, 100%), 1fr));
  gap: 0 var(--sp-3);
  align-items: start;
}

.due__row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-2) 0;
  min-height: var(--tap-comfortable);
  border: 0;
  border-bottom: 1px solid var(--color-border);
  background: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}
.due__row:last-child { border-bottom: 0; }
.due__row:active { background: var(--color-surface-2); }

.due__bar { width: 3px; align-self: stretch; border-radius: var(--r-full); flex-shrink: 0; }
.due__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.due__title { font-size: var(--fs-md); color: var(--color-text); }
.due__sub {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: var(--fs-xs);
  color: var(--color-text-muted);
}

/* ---- 보고 ---- */
.rep { display: flex; align-items: center; justify-content: space-between; gap: var(--sp-4); }
.rep__stats { display: flex; flex-wrap: wrap; gap: var(--sp-4); min-width: 0; }
.rep__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--fs-sm);
  color: var(--color-text-muted);
  white-space: nowrap;
}
.rep__chip b { color: var(--color-text); font-weight: var(--fw-semibold); }
.rep__dot { width: 7px; height: 7px; border-radius: var(--r-full); flex-shrink: 0; }
.rep__cta { flex-shrink: 0; min-height: 40px; }

/* ---- 스켈레톤 (primitives 의 .skel-row 를 쓴다) ---- */
.skel { display: flex; flex-direction: column; gap: var(--sp-3); }

/* 되돌릴 수 없는 승인/반려 버튼이다. .only-tablet-up 이 768px 부터 보이므로
   아이패드에서도 이 버튼을 손가락으로 누른다 — 34px 로 두면 안 된다. */
@media (max-width: 1023px) { /* --bp-tablet-max */
  .apvbtn { height: var(--tap-min); padding: 0 var(--sp-3); }
}

@media (max-width: 767px) { /* --bp-mobile-max */
  .today__greeting { font-size: var(--fs-xl); }
  .today__line { font-size: var(--fs-sm); }

  /* 4열 유지 + 컴팩트. 3열로 접으면 마지막 타일만 아래로 떨어져
     '결재 대기' 가 다른 화면처럼 보인다. */
  .tiles { gap: 6px; }
  .tile {
    min-height: 68px;
    padding: var(--sp-2) 6px var(--sp-2) 9px;
    border-radius: var(--r-md);
  }
  .tile__label { font-size: var(--fs-2xs); }
  .tile__value { font-size: var(--fs-title); }
  .tile__cap { display: none; }

  .agenda__gutter { width: 52px; font-size: var(--fs-xs); }
  .nowline__time { width: 52px; }
  .agenda__right { display: none; } /* 좁은 폭에서는 제목이 먼저다 — 상태는 상세에서 */

  .apv__wait { margin-left: auto; }

  .rep { flex-direction: column; align-items: stretch; gap: var(--sp-3); }
  .rep__stats { gap: var(--sp-3); }
  .rep__cta { width: 100%; min-height: 50px; }
}
</style>
